/**
 * Subscribes to a CSS media query.
 *
 * Most responsive behaviour belongs in CSS, and the board's layouts were built
 * that way — both rendered, one hidden by `lg:`. That is usually right, and it
 * was wrong here: dnd-kit registers a droppable per rendered column, so the
 * hidden layout's nine columns stayed registered under the same stage ids as
 * the visible ones, with zero-sized rectangles. Every keyboard drag then aimed
 * at a 0×0 rect at the origin and landed on whatever chip happened to be
 * nearest it.
 *
 * When a component's *identity* depends on the breakpoint — not merely its
 * appearance — the breakpoint has to reach JavaScript.
 *
 * Uses `useSyncExternalStore`, which is React's own answer for an external
 * mutable source: it subscribes, reads consistently during render, and gives a
 * server snapshot for the SSR case.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.5.9
 */
import { useCallback, useSyncExternalStore } from 'react';

/**
 * True while `query` matches.
 *
 * Returns false where `matchMedia` is unavailable — a server render, or a test
 * environment that has not stubbed it — which makes the narrow layout the
 * default. That is the safer default: it is the one that works at every width.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void): (() => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => undefined;
      }

      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);

      return () => {
        list.removeEventListener('change', onChange);
      };
    },
    [query],
  );

  const getSnapshot = useCallback((): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }

    return window.matchMedia(query).matches;
  }, [query]);

  /* The server snapshot must be a stable value, not a second read: React
     compares them and warns on a mismatch.

     `v8 ignore next` on the callback itself: React calls it only when hydrating
     a server-rendered tree, and this is a client-only SPA, so nothing in a test
     can reach it. It is kept because omitting it makes the hook unsafe for any
     future server render, and returning false there matches `getSnapshot`'s own
     answer when `matchMedia` is absent. */
  /* v8 ignore next */
  const getServerSnapshot = (): boolean => false;

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
